import React, { useEffect, useRef, useState } from "react";
import {
  Share2, Link2, MessageCircle, Send, Check, X, Facebook, Mail,
  Smartphone,
} from "lucide-react";
import {
  hasNativeInstallPrompt,
  isIOS,
  isStandalone,
  subscribeInstallPrompt,
  triggerInstallPrompt,
} from "../lib/installPrompt";
import { IosInstallModal, GenericInstallModal } from "./InstallButton";

const encodeShare = (platform, url, text) => {
  const u = encodeURIComponent(url);
  const t = encodeURIComponent(text);
  switch (platform) {
    case "x":
      return `https://twitter.com/intent/tweet?url=${u}&text=${t}`;
    case "whatsapp":
      return `https://wa.me/?text=${t}%20${u}`;
    case "telegram":
      return `https://t.me/share/url?url=${u}&text=${t}`;
    case "facebook":
      return `https://www.facebook.com/sharer/sharer.php?u=${u}`;
    case "email":
      return `mailto:?subject=${t}&body=${t}%0A%0A${u}`;
    default:
      return url;
  }
};

export const ShareButton = ({ url, title = "Shared from LAST", label = "Share", compact = false, large = false }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [installReady, setInstallReady] = useState(hasNativeInstallPrompt());
  const [installHelp, setInstallHelp] = useState(null); // 'ios' | 'generic' | null
  const popRef = useRef(null);

  useEffect(() => {
    const sync = () => setInstallReady(hasNativeInstallPrompt());
    sync();
    return subscribeInstallPrompt(sync);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (open && popRef.current && !popRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const tryNativeShare = async () => {
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ url, title });
        setOpen(false);
        return;
      } catch {
        // user cancelled or share failed — fall through to popover
      }
    }
    setOpen((v) => !v);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  // "Add to Home Screen" handler. Prefers the captured native browser
  // prompt (Chromium-based Android/desktop) so the visitor sees the
  // OS-level install dialog instead of a custom instructions page.
  // Falls back to a small modal on iOS Safari (which has no install
  // API) or other browsers that didn't fire `beforeinstallprompt`.
  const handleAddToHome = async () => {
    setOpen(false);
    if (isStandalone()) return; // already installed
    if (hasNativeInstallPrompt()) {
      const outcome = await triggerInstallPrompt();
      // Native dialog handled everything — no extra UI needed.
      if (outcome === "accepted" || outcome === "dismissed") return;
    }
    setInstallHelp(isIOS() ? "ios" : "generic");
  };

  const showAddToHome = !isStandalone();

  return (
    <div className="relative inline-flex" ref={popRef}>
      <button
        type="button"
        onClick={tryNativeShare}
        data-testid="share-button"
        aria-label={compact ? label : undefined}
        title={compact ? label : undefined}
        className={
          compact
            ? `inline-flex items-center justify-center rounded-full border border-black/10 bg-white text-[var(--brand-ink)] transition-colors hover:bg-[#0A0F17] hover:text-white sm:h-10 sm:w-10 ${
                large ? "h-11 w-11" : "h-6 w-6"
              }`
            : "inline-flex items-center gap-1.5 rounded-md border border-black/10 bg-white px-3 py-1.5 label-kicker text-[var(--brand-ink)] transition-colors hover:border-[#0A0F17]"
        }
        aria-haspopup="true"
        aria-expanded={open}
      >
        {compact ? (
          <Share2 size={large ? 20 : 16} strokeWidth={2.5} />
        ) : (
          <>
            <Share2 size={12} strokeWidth={2.5} /> {label}
          </>
        )}
      </button>

      {open ? (
        <div
          className="absolute right-0 top-full z-30 mt-2 w-56 overflow-hidden rounded-md border border-black/10 bg-white shadow-lg"
          data-testid="share-menu"
          role="menu"
        >
          <div className="flex items-center justify-between border-b border-black/5 px-3 py-2">
            <span className="label-kicker text-[var(--brand-body)]">Share this post</span>
            <button
              onClick={() => setOpen(false)}
              className="text-[var(--brand-body)] hover:text-[var(--brand-ink)]"
              aria-label="Close"
            >
              <X size={14} />
            </button>
          </div>
          <ul className="py-1 text-sm">
            <ShareItem
              testId="share-copy"
              label={copied ? "Link copied!" : "Copy link"}
              Icon={copied ? Check : Link2}
              onClick={copyLink}
              accent={copied ? "#6CABDD" : undefined}
            />
            <ShareItem
              testId="share-x"
              label="Share on X"
              Icon={Send}
              href={encodeShare("x", url, title)}
            />
            <ShareItem
              testId="share-whatsapp"
              label="Share on WhatsApp"
              Icon={MessageCircle}
              href={encodeShare("whatsapp", url, title)}
            />
            <ShareItem
              testId="share-facebook"
              label="Share on Facebook"
              Icon={Facebook}
              href={encodeShare("facebook", url, title)}
            />
            <ShareItem
              testId="share-telegram"
              label="Share on Telegram"
              Icon={Send}
              href={encodeShare("telegram", url, title)}
            />
            <ShareItem
              testId="share-email"
              label="Email"
              Icon={Mail}
              href={encodeShare("email", url, title)}
            />
            {showAddToHome ? (
              <ShareItem
                testId="share-add-to-home"
                label={installReady ? "Add to Home Screen" : "Add to Home Screen…"}
                Icon={Smartphone}
                onClick={handleAddToHome}
              />
            ) : null}
          </ul>
        </div>
      ) : null}

      {installHelp === "ios" ? (
        <IosInstallModal onClose={() => setInstallHelp(null)} />
      ) : null}
      {installHelp === "generic" ? (
        <GenericInstallModal onClose={() => setInstallHelp(null)} />
      ) : null}
    </div>
  );
};

const ShareItem = ({ label, Icon, onClick, href, testId, accent }) => {
  const className =
    "flex w-full items-center gap-3 px-3 py-2 font-display text-sm font-semibold text-[var(--brand-ink)] hover:bg-[#F8FAFC]";
  const style = accent ? { color: accent } : undefined;
  if (href) {
    return (
      <li>
        <a
          href={href}
          target="_blank"
          rel="noreferrer"
          data-testid={testId}
          className={className}
        >
          <Icon size={14} strokeWidth={2.2} />
          {label}
        </a>
      </li>
    );
  }
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        data-testid={testId}
        className={className}
        style={style}
      >
        <Icon size={14} strokeWidth={2.2} />
        {label}
      </button>
    </li>
  );
};
